import type { Application } from '@hotwired/stimulus';
import { Controller } from '@hotwired/stimulus';

/**
 *   This content is licensed according to the W3C Software License at
 *   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 * Adaption for Stimulus Copyright René Stalder <mail@renestalder.me>
 *
 * @description Tablist widget that implements ARIA Authoring Practices
 * @link https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 */
export class TablistController extends Controller {
    static targets = ['tab', 'panel'];

    static register(application: Application) {
        application.register('tablist', this);
    }

    declare readonly hasTabTarget: boolean;
    declare readonly tabTarget: HTMLElement;
    declare readonly tabTargets: HTMLElement[];

    declare readonly hasPanelTarget: boolean;
    declare readonly panelTarget: HTMLElement;
    declare readonly panelTargets: HTMLElement[];

    get firstTabElement() {
        return this.hasTabTarget ? this.tabTargets[0] : null;
    }

    get lastTabElement() {
        return this.hasTabTarget ? this.tabTargets[this.tabTargets.length - 1] : null;
    }

    pointerTrigger(e: PointerEvent) {
        const currentTab = e.currentTarget as HTMLElement;

        this.tabTargets.forEach((tab) => {
            if (tab !== currentTab) {
                tab.setAttribute('aria-selected', 'false');
                tab.tabIndex = -1;
            } else {
                currentTab.setAttribute('aria-selected', 'true');
                currentTab.removeAttribute('tabindex');
                currentTab.focus({ preventScroll: true });
            }
        });

        this.panelTargets.forEach((panel) => {
            if (panel.id === currentTab.getAttribute('aria-controls')) {
                panel.hidden = false;
            } else {
                panel.hidden = true;
            }
        });
    }

    moveFocusToTab(currentTab: HTMLElement) {
        currentTab.focus();
    }

    moveFocusToPreviousTab(currentTab: HTMLElement) {
        let index: number;

        if (currentTab === this.firstTabElement) {
            this.moveFocusToTab(this.lastTabElement);
        } else {
            index = this.tabTargets.indexOf(currentTab);
            this.moveFocusToTab(this.tabTargets[index - 1]);
        }
    }

    moveFocusToNextTab(currentTab: HTMLElement) {
        let index;

        if (currentTab === this.lastTabElement) {
            this.moveFocusToTab(this.firstTabElement);
        } else {
            index = this.tabTargets.indexOf(currentTab);
            this.moveFocusToTab(this.tabTargets[index + 1]);
        }
    }

    keyTrigger(e: KeyboardEvent) {
        const tgt = e.currentTarget as HTMLElement;
        let flag = false;

        switch (e.key) {
            case 'ArrowLeft':
                this.moveFocusToPreviousTab(tgt);
                flag = true;
                break;

            case 'ArrowRight':
                this.moveFocusToNextTab(tgt);
                flag = true;
                break;

            case 'Home':
                this.moveFocusToTab(this.firstTabElement);
                flag = true;
                break;

            case 'End':
                this.moveFocusToTab(this.lastTabElement);
                flag = true;
                break;

            default:
                break;
        }

        if (flag) {
            e.stopPropagation();
            e.preventDefault();
        }
    }
}

export default TablistController;
